/**
 * Consejos profesionales por capítulo ICAA, basados en
 * Robert Bahar — "A Fresh 2025 Introduction to Documentary Budgeting and Scheduling"
 * (International Documentary Association, dic. 2024).
 *
 * Adaptados a contexto español/ICAA por Eduardo & equipo grabarte.
 * Cada entrada describe el QUÉ es el capítulo, el RATIO/CRITERIO que recomienda
 * la industria internacional, y un CONSEJO práctico para evitar errores comunes.
 *
 * URL fuente:
 *   https://www.documentary.org/online-feature/2025-introduction-documentary-budgeting-and-scheduling
 */

export const BUDGET_TIPS = {
  "01": {
    title: "Guion y música",
    industry_label: "Story Rights · Research · Music",
    summary:
      "Aquí van los derechos de obra preexistente (libro, canción, archivo), el guion, los compositores y los músicos. No olvides reservar partida para investigación previa y, si es documental, para un equipo o jurista que despeje los derechos de archivo.",
    ratios: [
      "Música original: contrátala como flat fee con parámetros claros (nº de minutos finales, nº de instrumentos, nº de revisiones).",
      "Si licencias canciones existentes, presupuesta a un supervisor musical: el coste se dispara muy rápido.",
    ],
    tip:
      "Bahar (IDA 2025): «Cualquier propiedad intelectual u otros derechos que necesites optar o comprar — por ejemplo un libro, un artículo, una canción — va aquí.»",
  },
  "02": {
    title: "Personal artístico",
    industry_label: "Talent",
    summary:
      "Reparto, dobles, especialistas, voz en off. En documental clásico suele ser muy pequeño (sólo voz en off o reconstrucciones).",
    ratios: [
      "Si haces reenactments o contratas narrador profesional, mira tarifas sindicales actualizadas.",
      "Para documentales de creación o ensayo sin reparto profesional, este capítulo puede quedar a 0.",
    ],
    tip:
      "Si tu documental usa intérpretes (LSE, idiomas), valora si conviene presupuestarlos aquí o en accesibilidad de Cap. 03.",
  },
  "03": {
    title: "Equipo técnico",
    industry_label: "Producing Staff · Production Crew · Editorial Staff",
    summary:
      "Dirección, producción, fotografía, sonido, arte, eléctricos, montaje. El capítulo más sensible: aquí se nota la sostenibilidad del proyecto.",
    ratios: [
      "Dirección: entre 5% y 10% del presupuesto total (Bahar / IDA).",
      "Producción: entre 5% y 10% del presupuesto total (Bahar / IDA).",
      "Montaje: 1 mes de edición por cada 10 min de duración final del montaje (Alliance of Documentary Editors).",
      "Mantén paridad entre puestos similares (DOP/Sonidista, Ayudante Cámara/Microfonista).",
    ],
    tip:
      "Bahar recomienda anotar el desglose mensual de los honorarios planos: lo que parecía un fee grande suele ser un sueldo razonable repartido en muchos meses. Si la duración del proyecto cambia, renegocia el fee.",
  },
  "04": {
    title: "Escenografía",
    industry_label: "Art / Wardrobe / Make-up materials",
    summary:
      "Decorados, vestuario, atrezzo, maquillaje (materiales), efectos especiales. En documental observacional suele ser bajo; en reconstrucciones y ensayos visuales, sube.",
    ratios: [
      "Para documentales de archivo o verité puro, este capítulo puede ser casi cero.",
      "Si planeas reenactments, presupuesta vestuario y atrezzo realistas con asesor histórico.",
    ],
    tip:
      "Si necesitas efectos especiales en cámara (humo, fuego, agua) o coordinador de acción, presupuéstalo aquí — no en post.",
  },
  "05": {
    title: "Estudios y varios producción",
    industry_label: "Studio / Mix stage / Other",
    summary:
      "Platós, salas de visionado, estudios de mezcla y todo lo que no encaja exactamente en los otros capítulos.",
    ratios: [
      "Reserva al menos 1-2 sesiones de proyección en sala antes de cerrar el montaje y antes de la mezcla final.",
    ],
    tip:
      "Si haces sesiones de prueba con público para detectar fallos del montaje, presupuéstalas aquí. Es un coste pequeño con retorno enorme.",
  },
  "06": {
    title: "Maquinaria de rodaje y transportes",
    industry_label: "Equipment rental · Location transportation",
    summary:
      "Cámara, ópticas, sonido, iluminación, drones, vehículos de producción.",
    ratios: [
      "Para rodajes muy largos, comparar alquilar vs. comprar: si el alquiler total supera el 75% del coste de compra, plantéate comprar y revender (con permiso del financiador).",
      "Owner-operator (DOP/sonidista con su equipo) suele ser más barato y dar mejor resultado que alquilar a una rental.",
      "Regla 3-2-1 de Bahar para backups digitales: 3 copias, 2 medios distintos (disco + LTO), 1 fuera del lugar de rodaje.",
    ],
    tip:
      "Pregunta siempre por el seguro del equipo alquilado: a veces lo cubre la rental, a veces tu póliza de Cap.10. No lo des por hecho.",
  },
  "07": {
    title: "Viajes, dietas y comidas",
    industry_label: "Travel · Crew Meals",
    summary:
      "Vuelos, trenes, alojamiento, dietas y catering en rodaje.",
    ratios: [
      "Detalla cada viaje en la descripción (quién, dónde, por qué). Los financiadores escrutan los viajes con lupa.",
      "Catering: «Nada le dice más a tu equipo que respetas su trabajo que darles bien de comer» (Bahar). No recortes aquí.",
    ],
    tip:
      "Si grabas en una región remota, anota días reales de traslado (no son días de rodaje): el calendario y el presupuesto deben coincidir.",
  },
  "08": {
    title: "Película virgen / soportes digitales",
    industry_label: "Film & Lab / Hard Drives",
    summary:
      "Tarjetas de cámara, discos duros, LTO, soportes de sonido. Pequeño pero crítico.",
    ratios: [
      "Multiplica el tamaño de tarjeta por nº de cámaras × días de rodaje × ratio descarga/seguridad (suele ser 3× por la regla 3-2-1).",
    ],
    tip:
      "No improvises soportes: si un disco falla y no tienes el backup 3-2-1, pierdes material irreemplazable. El coste de discos es ridículo comparado con el riesgo.",
  },
  "09": {
    title: "Laboratorio / postproducción",
    industry_label: "Postproduction · VFX · Color · Sound · Music · Deliverables",
    summary:
      "Montaje (equipo y salas), color, VFX, gráficos, mezcla, música, máster final y entregas a festivales/distribución.",
    ratios: [
      "Calendario tipo de post: 12 semanas de procesos + 4 semanas de entregables/deliverables (Bahar).",
      "Si el proyecto es complejo (archivo extenso, VFX), planifica con cada vendor ANTES de rodar para detectar cuellos de botella.",
      "Color: rangos típicos entre 24h y 50h según ambición.",
      "Mezcla final: entre 3 y 10 días según ambición sonora.",
    ],
    tip:
      "«It's better to think of post as the second half of a marathon: you can't afford to carry too much baggage from the first half» (Bahar). Resuelve los problemas en rodaje, no en post.",
  },
  "10": {
    title: "Seguros e impuestos",
    industry_label: "Insurance · Taxes",
    summary:
      "Responsabilidad civil, equipo, accidentes, negativo/material rodado e impuestos asociados.",
    ratios: [
      "Tipos esenciales: general liability + production package (negative/equipment) + accidentes + E&O para distribución.",
      "E&O (Errors & Omissions): requerido por casi todos los distribuidores y plataformas. Tarda semanas en aprobarse. Prepáralo con tu abogado audiovisual.",
    ],
    tip:
      "Hablar con un corredor especializado en audiovisual ANTES del primer día de rodaje. Las primas dependen del riesgo: más vale tasarlo bien que provisionar a ciegas.",
  },
  "11": {
    title: "Gastos generales",
    industry_label: "Office & Administration",
    summary:
      "Alquiler de oficina, administración, gestoría, material, telecomunicaciones, gastos bancarios.",
    ratios: [
      "Límite ICAA: máximo 7% del presupuesto total.",
      "Buena práctica: presupuestar por meses (10 meses × Xexample €/mes) — más defendible que un «allow» global (Bahar).",
    ],
    tip:
      "Si la plataforma marca rojo este capítulo, pulsa «Ajustar con IA» para que el sistema redistribuya hasta el límite. Manten el 11% < 7% siempre.",
  },
  "12": {
    title: "Gastos de explotación, comercial y financieros",
    industry_label: "Publicity · Festivals · Distribution · Impact",
    summary:
      "Publicidad, promoción, festivales, mercados, distribución, ventas internacionales, gastos financieros y campañas de impacto.",
    ratios: [
      "Límite ICAA: máximo 20% del presupuesto total.",
      "Si haces «hybrid distribution» o «impact campaign», Bahar recomienda presupuestar al menos una consultoría inicial — el plan detallado va en presupuesto aparte.",
      "Reserva siempre algo para DCP de festival accesible (subtítulos quemados, audio descripción).",
    ],
    tip:
      "Define tu «finish line» (Bahar) antes de presupuestar este capítulo: ¿llegas hasta entrega a TV/plataforma, hasta premiere en festival, o hasta campaña de impacto completa? Cambia mucho el importe.",
  },
};

/** Cross-budget global tips, not chapter-specific. */
export const GLOBAL_TIPS = {
  contingency: {
    title: "Contingencia",
    body:
      "Bahar recomienda reservar entre 6% y 10% del total como imprevistos. En España, el ICAA limita a 7% (Cap.11), así que la práctica habitual es agotar el 7% y, si necesitas más colchón, distribuirlo en línea por línea entre el resto de capítulos.",
  },
  plans_abc: {
    title: "Plan A / B / C",
    body:
      "Borrador siempre el «Plan A» (presupuesto óptimo con tarifas profesionales) — es el que enseñas a financiadores. Luego, en uso interno, crea un Plan B (medio) y un Plan C (mínimo viable, mucho favor y autofinanciación). En grabarte puedes usar la barra de Versiones para crear estas variantes.",
  },
  phases: {
    title: "Fases del proyecto",
    body:
      "Aunque ICAA pide los 12 capítulos juntos, internamente te conviene saber cuánto va a Desarrollo / Producción / Montaje / Postproducción / Festivales y distribución. Cada fase tiene su propio momento de cierre de caja.",
  },
};

export const BUDGET_TIPS_SOURCE = {
  author: "Robert Bahar",
  year: "2025",
  title:
    "A Fresh Introduction to Documentary Budgeting and Scheduling (with New Templates!)",
  publisher: "International Documentary Association",
  url:
    "https://www.documentary.org/online-feature/2025-introduction-documentary-budgeting-and-scheduling",
};
